import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../../services/api';
import { CompanyNotificationSettings, CompanyUser, UpdateCompanyNotificationSettingsForm } from '../../types';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const defaultSettings: UpdateCompanyNotificationSettingsForm = {
  recipientUserIds: [],
  additionalEmails: [],
  emailOnWorkOrderCompleted: false,
  inAppOnWorkOrderCompleted: true,
  emailOnReadyToInvoice: true,
  inAppOnReadyToInvoice: true,
  emailOnNteExceeded: true,
  inAppOnNteExceeded: true,
  emailOnClientApprovalSent: false,
  inAppOnClientApprovalSent: true,
  emailOnClientApproved: true,
  inAppOnClientApproved: true,
  emailOnClientRejected: true,
  inAppOnClientRejected: true,
  emailOnWorkOrderCreatedFromEmail: false,
  inAppOnWorkOrderCreatedFromEmail: true,
  emailOnEmailImportNeedsReview: true,
  inAppOnEmailImportNeedsReview: true,
  emailOnEmailImportFailed: false,
  inAppOnEmailImportFailed: true,
};

const eventRows: Array<{
  label: string;
  emailKey: keyof UpdateCompanyNotificationSettingsForm;
  inAppKey: keyof UpdateCompanyNotificationSettingsForm;
}> = [
  { label: 'Work order marked Completed', emailKey: 'emailOnWorkOrderCompleted', inAppKey: 'inAppOnWorkOrderCompleted' },
  { label: 'Work order moved to Ready to Invoice', emailKey: 'emailOnReadyToInvoice', inAppKey: 'inAppOnReadyToInvoice' },
  { label: 'Estimate exceeds NTE', emailKey: 'emailOnNteExceeded', inAppKey: 'inAppOnNteExceeded' },
  { label: 'Client approval email sent', emailKey: 'emailOnClientApprovalSent', inAppKey: 'inAppOnClientApprovalSent' },
  { label: 'Client approved estimate', emailKey: 'emailOnClientApproved', inAppKey: 'inAppOnClientApproved' },
  { label: 'Client rejected estimate', emailKey: 'emailOnClientRejected', inAppKey: 'inAppOnClientRejected' },
  { label: 'Work order created from email', emailKey: 'emailOnWorkOrderCreatedFromEmail', inAppKey: 'inAppOnWorkOrderCreatedFromEmail' },
  { label: 'Email import needs review', emailKey: 'emailOnEmailImportNeedsReview', inAppKey: 'inAppOnEmailImportNeedsReview' },
  { label: 'Email import failed', emailKey: 'emailOnEmailImportFailed', inAppKey: 'inAppOnEmailImportFailed' },
];

const mapSettingsToForm = (data: CompanyNotificationSettings): UpdateCompanyNotificationSettingsForm => ({
  recipientUserIds: data.recipientUserIds ?? [],
  additionalEmails: data.additionalEmails ?? [],
  emailOnWorkOrderCompleted: data.emailOnWorkOrderCompleted,
  inAppOnWorkOrderCompleted: data.inAppOnWorkOrderCompleted,
  emailOnReadyToInvoice: data.emailOnReadyToInvoice,
  inAppOnReadyToInvoice: data.inAppOnReadyToInvoice,
  emailOnNteExceeded: data.emailOnNteExceeded,
  inAppOnNteExceeded: data.inAppOnNteExceeded,
  emailOnClientApprovalSent: data.emailOnClientApprovalSent,
  inAppOnClientApprovalSent: data.inAppOnClientApprovalSent,
  emailOnClientApproved: data.emailOnClientApproved,
  inAppOnClientApproved: data.inAppOnClientApproved,
  emailOnClientRejected: data.emailOnClientRejected,
  inAppOnClientRejected: data.inAppOnClientRejected,
  emailOnWorkOrderCreatedFromEmail: data.emailOnWorkOrderCreatedFromEmail,
  inAppOnWorkOrderCreatedFromEmail: data.inAppOnWorkOrderCreatedFromEmail,
  emailOnEmailImportNeedsReview: data.emailOnEmailImportNeedsReview,
  inAppOnEmailImportNeedsReview: data.inAppOnEmailImportNeedsReview,
  emailOnEmailImportFailed: data.emailOnEmailImportFailed,
  inAppOnEmailImportFailed: data.inAppOnEmailImportFailed,
});

interface CompanyNotificationSettingsPanelProps {
  standalone?: boolean;
}

const CompanyNotificationSettingsPanel: React.FC<CompanyNotificationSettingsPanelProps> = ({
  standalone = false,
}) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<UpdateCompanyNotificationSettingsForm>(defaultSettings);
  const [additionalEmailsText, setAdditionalEmailsText] = useState('');

  const { data: settingsResponse, isLoading } = useQuery({
    queryKey: ['company-notification-settings'],
    queryFn: () => apiService.getCompanyNotificationSettings(),
  });

  const { data: usersResponse } = useQuery({
    queryKey: ['company-users'],
    queryFn: () => apiService.getCompanyUsers(),
  });

  const users: CompanyUser[] = usersResponse?.data ?? [];

  useEffect(() => {
    if (settingsResponse?.success && settingsResponse.data) {
      const mapped = mapSettingsToForm(settingsResponse.data);
      setForm(mapped);
      setAdditionalEmailsText((mapped.additionalEmails ?? []).join('\n'));
    }
  }, [settingsResponse]);

  const saveMutation = useMutation({
    mutationFn: (data: UpdateCompanyNotificationSettingsForm) =>
      apiService.updateCompanyNotificationSettings(data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Notification settings saved');
        queryClient.invalidateQueries({ queryKey: ['company-notification-settings'] });
      } else {
        toast.error(response.message || 'Failed to save notification settings');
      }
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        'Failed to save notification settings';
      toast.error(message);
    },
  });

  const handleToggleUser = (userId: string) => {
    setForm((prev) => {
      const selected = new Set(prev.recipientUserIds);
      if (selected.has(userId)) {
        selected.delete(userId);
      } else {
        selected.add(userId);
      }
      return { ...prev, recipientUserIds: Array.from(selected) };
    });
  };

  const handleToggle = (key: keyof UpdateCompanyNotificationSettingsForm) => {
    setForm((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    const additionalEmails = additionalEmailsText
      .split(/[\n,;]+/)
      .map((email) => email.trim())
      .filter(Boolean);

    saveMutation.mutate({
      ...form,
      additionalEmails,
    });
  };

  const wrapperClass = standalone ? 'max-w-4xl' : 'max-w-4xl mt-8 scroll-mt-24';

  return (
    <div className={wrapperClass}>
      <div className="card">
        {!standalone && (
          <div className="card-header flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Workflow Notifications
          </div>
        )}
        <div className="card-body space-y-6">
          {isLoading ? (
            <div className="animate-pulse h-32 bg-gray-200 dark:bg-gray-700 rounded-lg" />
          ) : (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Choose who receives completion reports and workflow alerts. If no users are selected,
                company Admins are notified by default.
              </p>

              <div>
                <Label className="mb-2 block">Recipients (company users)</Label>
                <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
                  {users.length === 0 ? (
                    <p className="p-3 text-sm text-gray-500">No users found.</p>
                  ) : (
                    users.map((user) => (
                      <label
                        key={user.id}
                        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <input
                          type="checkbox"
                          checked={form.recipientUserIds.includes(user.id)}
                          onChange={() => handleToggleUser(user.id)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {user.firstName} {user.lastName}
                          <span className="text-gray-500 dark:text-gray-400"> — {user.email}</span>
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="additionalEmails">Additional email addresses</Label>
                <textarea
                  id="additionalEmails"
                  className="input mt-2 min-h-[80px]"
                  placeholder="owner@company.com&#10;accounting@company.com"
                  value={additionalEmailsText}
                  onChange={(e) => setAdditionalEmailsText(e.target.value)}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  One email per line (or comma-separated). These addresses receive email notifications only.
                </p>
              </div>

              <div>
                <Label className="mb-2 block">Notification events</Label>
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Event</th>
                        <th className="px-4 py-2 text-center font-medium text-gray-700 dark:text-gray-300">Email</th>
                        <th className="px-4 py-2 text-center font-medium text-gray-700 dark:text-gray-300">In-app</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventRows.map((row) => (
                        <tr key={row.label} className="border-t border-gray-200 dark:border-gray-700">
                          <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{row.label}</td>
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={Boolean(form[row.emailKey])}
                              onChange={() => handleToggle(row.emailKey)}
                              aria-label={`Email for ${row.label}`}
                            />
                          </td>
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={Boolean(form[row.inAppKey])}
                              onChange={() => handleToggle(row.inAppKey)}
                              aria-label={`In-app for ${row.label}`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end">
                <Button type="button" onClick={handleSave} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Saving...' : 'Save Notification Settings'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyNotificationSettingsPanel;
